
// ============================================================================
// STORAGE LAYER — Claude Artifact capabilities (db / assets / user)
// ============================================================================

const Cap = { db: null, assets: null, user: null, sample: null };
function setCaps(db, assets, user, sample) { Cap.db = db; Cap.assets = assets; Cap.user = user; Cap.sample = sample; }

async function withRetry(fn) {
  try { return await fn(); }
  catch (e) {
    if (e && e.code === "unavailable") {
      await new Promise((r) => setTimeout(r, 500 + Math.random() * 500));
      return await fn();
    }
    throw e;
  }
}

const boardsCol = () => Cap.db.collection("boards");
const mastersCol = () => Cap.db.collection("masters");
const templatesCol = () => Cap.db.collection("templates");
const liveCol = () => Cap.db.collection("live");

async function dbGetBoard(dateKey) {
  if (!Cap.db) return null;
  try { const snap = await withRetry(() => boardsCol().doc(dateKey).get()); return snap.exists ? snap.data() : null; }
  catch (e) { return null; }
}
async function dbSetBoard(dateKey, board) {
  if (!Cap.db) return false;
  try { await withRetry(() => boardsCol().doc(dateKey).set({ ...board, date: dateKey, updatedAt: Date.now() })); return true; }
  catch (e) { return false; }
}
async function dbDeleteBoard(dateKey) {
  if (!Cap.db) return false;
  try { await boardsCol().doc(dateKey).delete(); return true; } catch (e) { return false; }
}
async function dbGetMaster(wdId) {
  if (!Cap.db) return null;
  try { const snap = await withRetry(() => mastersCol().doc(wdId).get()); return snap.exists ? snap.data() : null; }
  catch (e) { return null; }
}
// What an un-edited day of this weekday shows: the template the weekday is
// linked to (masters/<wd>.templateId), else the weekday master itself.
// Returns { board, templateId, templateName } or null.
async function dbGetWeekdayDefault(wdId) {
  const master = await dbGetMaster(wdId);
  if (!master) return null;
  const { templateId, ...own } = master;
  if (templateId) {
    const tpl = await dbGetTemplate(templateId);
    if (tpl) return { board: tpl, templateId, templateName: tpl.name || templateId };
  }
  return { board: own, templateId: null, templateName: null };
}
async function dbSetMaster(wdId, board) {
  if (!Cap.db) return false;
  try { await withRetry(() => mastersCol().doc(wdId).set({ ...board, updatedAt: Date.now() })); return true; }
  catch (e) { return false; }
}
async function dbListTemplates() {
  if (!Cap.db) return [];
  try {
    const snap = await withRetry(() => templatesCol().orderBy("updatedAt", "desc").get());
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) { return []; }
}
async function dbGetTemplate(id) {
  if (!Cap.db) return null;
  try { const snap = await withRetry(() => templatesCol().doc(id).get()); return snap.exists ? snap.data() : null; }
  catch (e) { return null; }
}
async function dbSetTemplate(id, board) {
  if (!Cap.db) return false;
  try { await withRetry(() => templatesCol().doc(id).set({ ...board, updatedAt: Date.now() })); return true; }
  catch (e) { return false; }
}
async function dbCreateTemplate(board) {
  const ref = await templatesCol().add({ ...board, updatedAt: Date.now() });
  return ref.id;
}
async function dbDeleteTemplate(id) {
  if (!Cap.db) return false;
  try { await templatesCol().doc(id).delete(); return true; } catch (e) { return false; }
}
async function dbGetLive(dateKey) {
  if (!Cap.db) return null;
  try { const snap = await liveCol().doc(dateKey).get(); return snap.exists ? snap.data() : null; } catch (e) { return null; }
}
async function dbSetLive(dateKey, data) {
  if (!Cap.db) return false;
  try { await liveCol().doc(dateKey).set({ ...data, date: dateKey }); return true; } catch (e) { return false; }
}
function dbSubscribeLive(dateKey, onNext, onErr) {
  if (!Cap.db) return () => {};
  return liveCol().doc(dateKey).onSnapshot((snap) => onNext(snap.exists ? snap.data() : null), onErr);
}
// which days in a range have their own board — just the ids, not every
// board's content (select() is a server extension; see platform/api.js)
function boardsInRange(startKey, endKeyExcl) {
  const q = boardsCol().where("date", ">=", startKey).where("date", "<", endKeyExcl);
  return q.select ? q.select("date") : q;
}
function dbSubscribeMonthBoards(startKey, endKeyExcl, onNext, onErr) {
  if (!Cap.db) return () => {};
  return boardsInRange(startKey, endKeyExcl).onSnapshot(onNext, onErr);
}
async function dbCountBoardsInRange(startKey, endKeyExcl) {
  if (!Cap.db) return 0;
  try {
    const snap = await withRetry(() => boardsInRange(startKey, endKeyExcl).get());
    return snap.size;
  } catch (e) { return 0; }
}
function dbSubscribeLiveActive(onNext, onErr) {
  if (!Cap.db) return () => {};
  return liveCol().where("status", "==", "live").onSnapshot(onNext, onErr);
}

// ---- images: stored via the assets capability, id kept on the cue/block ----
function resizeImageToBlob(file, maxDim = 640, quality = 0.75) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        let { width, height } = img;
        if (width > height) { if (width > maxDim) { height = Math.round(height * (maxDim / width)); width = maxDim; } }
        else if (height > maxDim) { width = Math.round(width * (maxDim / height)); height = maxDim; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))), "image/jpeg", quality);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
async function uploadCueImage(file) {
  if (!Cap.assets) throw new Error("no-write-access");
  const blob = await resizeImageToBlob(file);
  const res = await Cap.assets.upload(blob, { type: "image/jpeg" });
  return res.id;
}
function deleteCueImage(id) {
  if (!Cap.assets || !id) return;
  Cap.assets.delete(id).catch(() => {});
}
// "/_blob/" is prefixed with window.__RUNDOWN_BASE__ (auto-detected by
// index.html from the page's own URL — see that file) so uploaded images
// still resolve correctly when this module is mounted under a path prefix
// inside a larger site, not just when served from the domain root.
//
// The result is placed inside CSS url(...), so only well-formed asset ids
// are accepted and characters that could end the url() early are escaped —
// a crafted "image id" in a shared rundown can't make every viewer's
// browser load something from another site.
function bgUrl(id) {
  if (!id || typeof id !== "string" || !/^[A-Za-z0-9-]{1,64}$/.test(id)) return "";
  const base = (typeof window !== "undefined" && window.__RUNDOWN_BASE__) || "";
  return (base + "/_blob/" + id).replace(/[()'"\\\s]/g, (c) => "%" + c.charCodeAt(0).toString(16).toUpperCase());
}

// ---- Photo Library: named, "locked" photos reusable across every cue in
// every Template/Board — each name is a unique key. A library entry NEVER
// shares its underlying asset id with a cue/block: applying a library photo
// to a cue, or locking a cue's current photo into the library, always makes
// a fresh copy of the image bytes first. That keeps every db row pointing at
// its own private asset id, which matters because asset deletion is
// irreversible and artifact-wide — if a cue and a library entry ever pointed
// at the same id, removing the image from one would silently break the
// other.
const photoLibraryCol = () => Cap.db.collection("photoLibrary");
function normalizePhotoName(s) { return String(s || "").trim().toLowerCase().replace(/\s+/g, " "); }
async function dbListPhotoLibrary() {
  if (!Cap.db) return [];
  try {
    const snap = await withRetry(() => photoLibraryCol().orderBy("name").get());
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) { return []; }
}
async function dbCreatePhotoEntry(entry) {
  const ref = await photoLibraryCol().add({ ...entry, createdAt: Date.now() });
  return ref.id;
}
async function dbDeletePhotoEntry(id) {
  if (!Cap.db) return false;
  try { await photoLibraryCol().doc(id).delete(); return true; } catch (e) { return false; }
}
async function dbUpdatePhotoEntry(id, patch) {
  if (!Cap.db) return false;
  try { await photoLibraryCol().doc(id).update(patch); return true; } catch (e) { return false; }
}
// live subscription so the library page (and every "pick from library"
// popover) picks up photos added, renamed, or removed elsewhere — another
// tab, another person, or seeded directly into the database — without
// anyone needing to manually refresh the page.
function dbSubscribePhotoLibrary(onNext, onErr) {
  if (!Cap.db) return () => {};
  return photoLibraryCol().orderBy("name").onSnapshot((snap) => onNext(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), onErr);
}
// duplicate an already-stored image into a brand-new, independently-owned asset
async function copyAssetImage(sourceId) {
  if (!Cap.assets || !sourceId) throw new Error("no-write-access");
  const res = await fetch(bgUrl(sourceId));
  if (!res || !res.ok) throw new Error("copy-fetch-failed");
  const blob = await res.blob();
  const uploaded = await Cap.assets.upload(blob, { type: blob.type || "image/jpeg" });
  return uploaded.id;
}

// ---- Cue pool: reusable VTR / IMMERSIVE / activity / sponsor cues that get
// dragged into a board's columns as blocks
const cuePoolCol = () => Cap.db.collection("cuePool");
function dbSubscribeCuePool(onNext, onErr) {
  if (!Cap.db) { onNext([]); return () => {}; }
  return cuePoolCol().orderBy("order").onSnapshot((snap) => onNext(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), onErr);
}
async function dbCreatePoolItem(item) { const ref = await cuePoolCol().add({ ...item, createdAt: Date.now() }); return ref.id; }
async function dbUpdatePoolItem(id, patch) { try { await cuePoolCol().doc(id).update(patch); return true; } catch (e) { return false; } }
async function dbDeletePoolItem(id) { try { await cuePoolCol().doc(id).delete(); return true; } catch (e) { return false; } }

// sponsors on the LED RING & LED T — one doc per show category (promoter /
// rko / rws): { fight: [row], break: [row], fightTotal, breakTotal, updatedAt }
const sponsorsCol = () => Cap.db.collection("sponsors");
function dbSubscribeSponsors(id, onNext, onErr) {
  if (!Cap.db) { onNext(null); return () => {}; }
  return sponsorsCol().doc(id).onSnapshot((snap) => onNext(snap.exists ? snap.data() : null), onErr);
}
async function dbSetSponsors(id, data) {
  if (!Cap.db) return false;
  try { await withRetry(() => sponsorsCol().doc(id).set({ ...data, updatedAt: Date.now() })); return true; }
  catch (e) { return false; }
}

export { cuePoolCol, sponsorsCol, dbSubscribeSponsors, dbSetSponsors, dbSubscribeCuePool, dbCreatePoolItem, dbUpdatePoolItem, dbDeletePoolItem, Cap, setCaps, withRetry, boardsCol, mastersCol, templatesCol, liveCol, dbGetBoard, dbSetBoard, dbDeleteBoard, dbGetMaster, dbGetWeekdayDefault, dbSetMaster, dbListTemplates, dbGetTemplate, dbSetTemplate, dbCreateTemplate, dbDeleteTemplate, dbGetLive, dbSetLive, dbSubscribeLive, dbSubscribeMonthBoards, dbCountBoardsInRange, dbSubscribeLiveActive, resizeImageToBlob, uploadCueImage, deleteCueImage, bgUrl, photoLibraryCol, normalizePhotoName, dbListPhotoLibrary, dbCreatePhotoEntry, dbDeletePhotoEntry, dbUpdatePhotoEntry, dbSubscribePhotoLibrary, copyAssetImage };
